// @ts-check

import { get_all_form_assignments } from '~/api/zero-api';
import { AssignmentActionCell, AssignmentNameCell, AssignmentStatusCell } from '~/components/Forms/AssignmentDataGrid/AssignmentDataGrid.cells';
import { AgGridFilter } from '~/other/agGridHelper';
import { dateFormatterNoTime, formatName } from '~/other/Helper';
import { memoize } from "lodash-es";
import { generateDataGridEventNames } from '~/components/Shared/ZeroDataGrid/ZeroDataGrid.hooks';

export const EventNames = generateDataGridEventNames('AssignmentDataGrid');

export const DEFAULT_SORT_MODEL = [
    {
        colId: "due_date_timestamp",
        sort: "desc",
    },
];

export function getSortByColumnName(colName) {
    switch (colName) {
        case 'agAssignedToName': return 'user';
        case 'form_name': return 'form';
        case 'scheduler_name': return 'scheduler';
        case 'due_date_timestamp': return 'due_date';
        default: return colName;
    }
}

/**
 * @param {string} pageType
 * @param {boolean} isAdminOrTeamLead
 * @returns {AgGrid.GridOptions['columnDefs']}
 */
export function getColumnDefs(pageType, isAdminOrTeamLead) {
    /** @type {AgGrid.GridOptions['columnDefs']} */
    let defs = [
        isAdminOrTeamLead && {
            headerName: "",
            field: "_actions_",
            width: 50,
            cellRenderer: AssignmentActionCell,
            resizable: false,
            sortable: false,
            suppressMovable: true,
            lockPosition: "left",
            cellStyle: {
                padding: 0,
            }
        },
        {
            headerName: "Assigned To",
            field: "agAssignedToName",
            width: 155,
            cellRenderer: AssignmentNameCell,
            valueFormatter: ({data}) => {
                if (!data?.assigned_to) {
                    return "";
                }
                return formatName(data.assigned_to, {defaultName: ""});
            },
            ...AgGridFilter.textNoBlankOptions,
        },
        {
            headerName: pageType === "lms" ? "Course" : "Template",
            field: "form_name",
            width: 165,
            cellClass: 'ag-grid-normal-lineheight',
            ...AgGridFilter.textNoBlankOptions,
        },
        {
            headerName: "Schedule",
            field: "scheduler_name",
            width: 165,
            cellClass: 'ag-grid-normal-lineheight',
            ...AgGridFilter.textNullable,
        },
        {
            headerName: "Due Date",
            field: "due_date_timestamp",
            width: 160,
            valueFormatter: ({ value }) => value ? dateFormatterNoTime(value) : "",
            cellClass: "dateFormatterNoTime",
            initialSort: 'desc',
            ...AgGridFilter.date,
        },
        {
            headerName: "Status",
            field: "status",
            width: 110,
            sortable: false,
            cellRenderer: AssignmentStatusCell,
            valueFormatter: ({data}) => {
                if (!data) return "";
                return getAssignmentStatusText(data);
            }
        },
        {
            headerName: "Assigned On",
            field: "assigned_at",
            width: 195,
            valueFormatter: ({ value }) => value ? dateFormatterNoTime(value) : "",
            cellClass: "dateFormatterNoTime",
            ...AgGridFilter.date,
        },
        {
            headerName: "Completed On",
            field: "completed_at",
            width: 195,
            valueFormatter: ({ value }) => value ? dateFormatterNoTime(value) : "",
            cellClass: "dateFormatterNoTime",
            ...AgGridFilter.date,
        },
    ];

    return defs.filter(def => !!def);
}

/**
 * @param {URLSearchParams} queryParams 
 * @returns {Promise<{assignments: any[], totalAssignments: number}>}
 */
async function _loadAssignments(queryParams) {
    const res = await get_all_form_assignments(`?${queryParams}`);
    const { assignments: rawAssignments, total: totalAssignments } = await res.json();

    const assignments = rawAssignments.map(assignment => {
        return {
            ...assignment,
            agAssignedToName: formatName(assignment.assigned_to, {defaultName: ""}),
        }
    });

    return {assignments, totalAssignments};
}

/** @type {typeof _loadAssignments} */
// @ts-ignore
export const loadAssignments = memoize(_loadAssignments, (queryParams) => queryParams.toString());

/**
 * Get assignment status text based on assignment data
 * @param {any} assignment 
 * @returns {string}
 */
export function getAssignmentStatusText(assignment) {
    if (assignment.shared_submission_uuid) {
        return "Completed";
    } else if (assignment.completed) {
        return "Completed";
    } else if (assignment.excused) {
        return "Excused";
    } else if (assignment.due_date_timestamp && new Date(assignment.due_date_timestamp * 1000) < new Date()) {
        return "Missed";
    } else {
        return "Open";
    }
}

/**
 * 
 * @param {any} assignment 
 * @param {string} orgId 
 */
export function getAssignmentSubmissionLink(assignment, orgId) {
    if (!assignment.submission_uuid) {
        return null;
    }
    
    let team_uuid = 'my_teams';
    if (assignment.teams && assignment.teams.length > 0) {
        team_uuid = assignment.teams[0].uuid;
    }
    
    const form_type = assignment.form_type === 3 ? 'courses' : 'forms';
    return `/${orgId}/home/team/${team_uuid}/${form_type}/submission_view/${assignment.submission_uuid}`;
} 